import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import OnboardingBottomNav from '../../components/common/OnboardingBottomNav';
import { saveOnboardingData } from '../../api/onboardingService';
import groupService from '../../api/groupService';
import authService from '../../api/authService';
import { useGroup } from '../../context/GroupContext';
import { useAuth } from '../../context/AuthContext';
import '../../styles/onboarding/Onboarding.css';

const Yartzheits = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { refreshUserGroups } = useGroup();
  const { logout, refreshAuthToken } = useAuth(); // Get auth context functions
  
  // Initialize with a default Yartzheit A that has no default relationship selection
  const defaultYartzheit = { id: 1, relationship: '', date: '' };
  const [yartzheits, setYartzheits] = useState([defaultYartzheit]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Debug log to check if the component renders with initial data
  console.log('Initial yartzheits state:', yartzheits);

  useEffect(() => {
    // Load any previously saved yartzheits data if available
    const savedYartzheits = localStorage.getItem('yartzheits');
    if (savedYartzheits) {
      try {
        const parsed = JSON.parse(savedYartzheits);
        console.log('Loaded saved yartzheits:', parsed);
        // Only use saved yartzheits if there's at least one entry
        if (parsed && parsed.length > 0) {
          setYartzheits(parsed);
        } else {
          // Ensure we have at least one default entry
          setYartzheits([defaultYartzheit]);
        }
      } catch (error) {
        console.error('Error parsing saved yartzheits data:', error);
        // Ensure we have at least one entry if parsing fails
        setYartzheits([defaultYartzheit]);
      }
    } else {
      console.log('No saved yartzheits found, using default');
      // Ensure the default entry is set
      setYartzheits([defaultYartzheit]);
    }
  }, []);

  const addAnotherYartzheit = () => {
    if (yartzheits.length < 15) {
      const newYartzheitId = yartzheits.length + 1;
      setYartzheits([...yartzheits, { id: newYartzheitId, relationship: '', date: '' }]);
    }
  };

  const handleInputChange = (id, field, value) => {
    const updatedYartzheits = yartzheits.map(yartzheit => 
      yartzheit.id === id ? { ...yartzheit, [field]: value } : yartzheit
    );
    setYartzheits(updatedYartzheits);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // If dateString is already in yyyy-MM-dd format, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    
    // Try to convert MM/DD/YYYY to yyyy-MM-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return dateString;
  };

  const handleDateChange = (id, value) => {
    // Store date in yyyy-MM-dd format internally
    handleInputChange(id, 'date', value);
  };

  // Function to collect all onboarding data and save to database
  const completeOnboarding = async (skipCurrentStep = false) => {
    try {
      setLoading(true);
      
      // Check for authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Authentication token is missing');
        alert('You appear to be logged out. Please log in again to complete the onboarding process.');
        navigate('/');
        return;
      }
      
      // Try to refresh the auth token first to ensure it's valid
      try {
        console.log('Attempting to refresh auth token before saving onboarding data');
        await refreshAuthToken();
      } catch (refreshError) {
        console.error('Failed to refresh token, but continuing anyway:', refreshError);
      }
      
      // Log more information for debugging
      console.log('Starting completeOnboarding with skipCurrentStep:', skipCurrentStep);
      console.log('Current yartzheits state:', yartzheits);
      
      // Save yartzheits data to local storage - only include entries that have BOTH relationship and date
      // When skipping, include empty entries
      const validYartzheits = skipCurrentStep ? [] : yartzheits.filter(yartzheit => 
        yartzheit.relationship.trim() !== '' && yartzheit.date.trim() !== ''
      );
      
      console.log('Filtered valid yartzheits:', validYartzheits);
      localStorage.setItem('yartzheits', JSON.stringify(validYartzheits));
      
      // Collect all onboarding data
      const familyStatus = localStorage.getItem('familyStatus') || '';
      const anniversary = localStorage.getItem('anniversaryDate') || '';
      
      // Get user identity information from earlier steps
      let firstName = '';
      let lastName = '';
      let address = '';
      let city = '';
      let state = '';
      let zipCode = '';
      let phone = '';
      let birthdate = '';
      
      try {
        const identityDataString = localStorage.getItem('identityData');
        console.log('Raw identity data from localStorage:', identityDataString);
        
        if (identityDataString) {
          const identityData = JSON.parse(identityDataString);
          console.log('Parsed identity data:', identityData);
          
          // 🔧 FIX: Ensure all fields are properly extracted and mapped
          firstName = identityData.firstName || '';
          lastName = identityData.lastName || '';
          address = identityData.street || '';  // Form uses 'street', server expects 'address'
          city = identityData.city || '';
          state = identityData.state || '';
          zipCode = identityData.zipCode || '';
          phone = identityData.phoneNumber || ''; // Form stores as 'phoneNumber'
          birthdate = identityData.dateOfBirth || ''; // Form stores as 'dateOfBirth'
          
          console.log('🔧 FIXED: Extracted identity data with proper field mapping:', { 
            firstName, lastName, address, city, state, zipCode, phone, birthdate 
          });
        } else {
          console.warn('❌ ERROR: No identity data found in localStorage - this means the identity verification step was skipped or failed');
        }
      } catch (parseError) {
        console.error('❌ ERROR: Error parsing identity data:', parseError);
      }
      
      // Load kids birthdays if available
      let kidsBirthdays = [];
      try {
        const savedChildren = localStorage.getItem('children');
        console.log('Raw children data from localStorage:', savedChildren);
        
        if (savedChildren) {
          kidsBirthdays = JSON.parse(savedChildren);
          console.log('Parsed children data:', kidsBirthdays);
        }
      } catch (error) {
        console.error('Error parsing saved children data:', error);
      }
      
      // 🔧 FIX: Prepare onboarding data with explicit field validation
      const onboardingData = {
        firstName: firstName || null,
        lastName: lastName || null,
        address: address || null,
        city: city || null,
        state: state || null,
        zipCode: zipCode || null,
        phone: phone || null,
        birthdate: birthdate || null,
        familyStatus: familyStatus || null,
        anniversary: anniversary || null,
        kidsBirthdays: kidsBirthdays || [],
        yartzeits: validYartzheits || [],
        groupId,
        onboardingCompleted: true
      };
      
      console.log('🔧 FIXED: Sending onboarding data to server with null values for empty fields:', onboardingData);
      
      // Validate that we have at least some identity data
      if (!firstName && !lastName && !address && !city && !state && !zipCode && !phone && !birthdate) {
        console.error('❌ CRITICAL: No identity data found - user may have skipped identity verification');
        console.log('Available localStorage keys:', Object.keys(localStorage));
      }
      
      // First request to join the group - do this BEFORE saving onboarding data
      try {
        const joinResult = await groupService.joinGroup(groupId);
        if (joinResult.already_joined) {
          console.log('Already a member of group:', groupId);
        } else if (joinResult.already_requested) {
          console.log('Already requested to join group:', groupId, 'Status:', joinResult.status);
        } else {
          console.log('Successfully requested to join group:', groupId);
          console.log('Your request is pending admin approval.');
        }
      } catch (groupError) {
        console.error('Error requesting to join group:', groupError);
        // Continue with onboarding even if group join request fails
      }
      
      // Then save all collected onboarding data to the database
      const result = await saveOnboardingData(onboardingData);
      console.log('✅ SUCCESS: Onboarding data saved successfully:', result);
      
      // Mark onboarding as completed
      await authService.updateOnboardingStatus(true);
      console.log('Onboarding status updated successfully');
      
      // Refresh user groups
      try {
        await refreshUserGroups();
        console.log('User groups refreshed successfully');
      } catch (refreshError) {
        console.error('Error refreshing user groups:', refreshError);
        // Continue even if refresh fails
      }
      
      // Show success message
      setSuccessMessage('Profile completed successfully! Your request to join the group has been sent to the admin for approval. You will be notified once approved. Redirecting to home page...');
      setShowSuccess(true);
      
      // Clean up localStorage data
      localStorage.removeItem('familyStatus');
      localStorage.removeItem('anniversaryDate');
      localStorage.removeItem('children');
      localStorage.removeItem('yartzheits');
      localStorage.removeItem('identityData');
      
      // Wait a bit to show the success message before redirecting
      setTimeout(() => {
        // Navigate to home page instead of group page since user needs approval
        navigate('/');
      }, 3000);
      
    } catch (error) {
      console.error('❌ ERROR: Error completing onboarding:', error);
      console.error('Error details:', error.response?.data || 'No detailed error information');
      
      // Handle authentication errors without automatic logout
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.log('Authentication error during onboarding completion, attempting token refresh');
        
        try {
          await refreshAuthToken();
          // Try once more after refreshing the token
          console.log('Token refreshed, retrying onboarding completion');
          return completeOnboarding(skipCurrentStep);
        } catch (refreshError) {
          console.error('Failed to refresh authentication token:', refreshError);
          alert('Your session appears to have expired. Please log in again to complete the onboarding process.');
          
          // Try to redirect to login page after a delay
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      } else {
        // More specific error message
        const errorMessage = error.response?.data?.message || 'There was an error completing the onboarding process. Please try again.';
        alert(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    completeOnboarding(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSkip = () => {
    completeOnboarding(true);
  };

  // Available relationship options for the dropdown
  const relationshipOptions = [
    'Father', 'Mother', 'Grandfather', 'Grandmother', 
    'Brother', 'Sister', 'Spouse', 'Child', 'Other'
  ];

  // Progress dots renderer
  const renderProgressDots = () => {
    const totalSteps = 5; // Total steps in the onboarding flow
    const currentStep = 5; // Yartzheits is the final step
    const dots = [];
    
    for (let i = 0; i < totalSteps; i++) {
      dots.push(
        <div 
          key={i} 
          className={`progress-dot ${i + 1 <= currentStep ? 'active' : ''}`}
        ></div>
      );
    }
    
    return dots;
  };

  // Check if form is valid
  const isFormValid = yartzheits.some(yartzheit => 
    yartzheit.relationship.trim() !== '' && yartzheit.date.trim() !== ''
  );

  return (
    <div className="onboarding-container">
      {showSuccess ? (
        <div className="success-message-container">
          <div className="success-message">
            <div className="celebration-icon">🎉</div>
            <h2>{successMessage}</h2>
          </div>
        </div>
      ) : (
        <>
          <div className="onboarding-content">
            <h1 className="yartzheits-title">Add Important Yartzeit's</h1>
            <p className="yartzheits-description">
              Your shul doesn't want to miss your important dates—yes, even Yartzeits. Let's keep them in the loop!
            </p>

            <div className="yartzheits-form">
              {yartzheits.length > 0 ? (
                yartzheits.map((yartzheit) => (
                  <div key={yartzheit.id} className="yartzheit-details">
                    <h2 className="yartzheit-label">Yartzeit {String.fromCharCode(64 + yartzheit.id)}</h2>
                    <div className="yartzheit-input-group">
                      <div className="relationship-field">
                        <select
                          value={yartzheit.relationship || ''}
                          onChange={(e) => handleInputChange(yartzheit.id, 'relationship', e.target.value)}
                          className="form-input"
                        >
                          <option value="" disabled>Select relationship</option>
                          {relationshipOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="date"
                        placeholder="MM/DD/YYYY"
                        value={formatDate(yartzheit.date || '')}
                        onChange={(e) => handleDateChange(yartzheit.id, e.target.value)}
                        className="form-input date-input"
                        max={new Date().toISOString().split('T')[0]} // Prevent future dates
                        style={{display: 'block', width: '100%'}} /* Force display */
                      />
                    </div>
                  </div>
                ))
              ) : (
                // If somehow there are still no entries, add the default one immediately
                <div className="yartzheit-details">
                  <h2 className="yartzheit-label">Yartzeit A</h2>
                  <div className="yartzheit-input-group">
                    <div className="relationship-field">
                      <select
                        value=""
                        onChange={(e) => handleInputChange(1, 'relationship', e.target.value)}
                        className="form-input"
                      >
                        <option value="" disabled>Select relationship</option>
                        {relationshipOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="date"
                      placeholder="MM/DD/YYYY"
                      value=""
                      onChange={(e) => handleDateChange(1, e.target.value)}
                      className="form-input date-input"
                      max={new Date().toISOString().split('T')[0]}
                      style={{display: 'block', width: '100%'}}
                    />
                  </div>
                </div>
              )}
              
              {yartzheits.length < 15 && (
                <button 
                  className="add-yartzheit-button" 
                  onClick={addAnotherYartzheit}
                >
                  Add another Yartzeit
                </button>
              )}
            </div>
          </div>

          {/* Progress dots */}
          <div className="fixed-progress-dots">
            {renderProgressDots()}
          </div>

          {/* Skip button */}
          <div className="skip-button-container">
            <button 
              className="skip-button" 
              onClick={handleSkip}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Skip'}
            </button>
          </div>

          {/* Use the OnboardingBottomNav component for consistent navigation */}
          <OnboardingBottomNav
            onBack={handleBack}
            onNext={handleDone}
            nextDisabled={loading}
            nextLabel={loading ? 'Processing...' : 'Done'}
          />
        </>
      )}
    </div>
  );
};

export default Yartzheits;